import { c as _c } from "react/compiler-runtime";
import { graphql } from "@opensea/graphql";
import { readFragment } from "gql.tada";
import { useQuery } from "urql";
import { useTranslations } from "use-intl";
import { ActionTimeline, ActionTimelineError, ActionTimelineFragment, ActionTimelineSkeleton } from "../../../../components/ActionTimeline";
import { CrossEcosystemSwapNotification } from "../../../../components/CrossEcosystemSwapNotification";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants";
import { useAddressCompatibility } from "../../../../hooks/useAddressCompatibility";
import { useIsAbstractWalletAccount } from "../../../../hooks/useIsAbstractWalletAccount";
import { useAddress } from "../../../../polychain/hooks/useAddress";
import { useActionErrors } from "../../../hooks/useErrors";
import { useSetBlockchainActionErrors } from "../../../hooks/useSetBlockchainActionErrors";
import { useSetBlockchainActionErrorsFragment } from "../../../hooks/useSetBlockchainActionErrors.fragment";
import { MintActionTimelineDropFragment } from "./MintActionTimelineDrop.fragment";
export const MintActionTimelineFragment = graphql(`
  fragment MintActionTimeline on Collection {
    slug
    address
    chain {
      identifier
    }
  }
`);
const MintActionTimelineQuery = graphql(`
    query MintActionTimelineQuery(
      $address: Address!
      $fromAssets: [AssetQuantityInput!]!
      $toAssets: [AssetQuantityInput!]!
      $recipient: Address
    ) {
      swap(
        address: $address
        fromAssets: $fromAssets
        toAssets: $toAssets
        recipient: $recipient
        action: MINT
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `, [ActionTimelineFragment, useSetBlockchainActionErrorsFragment]);
export function MintActionTimeline(t0) {
  const $ = _c(42);
  const {
    collection: collectionKey,
    drop: dropKey,
    tokenInputs,
    selectedCurrency,
    selectedCurrencyLoading,
    onComplete,
    walletDestination
  } = t0;
  const t = useTranslations("wallet.MintActionTimeline");
  let t1;
  if ($[0] !== collectionKey) {
    t1 = readFragment(MintActionTimelineFragment, collectionKey);
    $[0] = collectionKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const collection = t1;
  readFragment(MintActionTimelineDropFragment, dropKey);
  const address = useAddress();
  const isAbstractWallet = useIsAbstractWalletAccount();
  const isCrossChain = Boolean(selectedCurrency && selectedCurrency.chainIdentifier !== collection.chain.identifier);
  const isAddressCompatible = useAddressCompatibility(walletDestination?.address ?? address, collection.chain.identifier);
  let t2;
  if ($[2] !== tokenInputs) {
    t2 = tokenInputs.map(_temp);
    $[2] = tokenInputs;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const toAssets = t2;
  let t3;
  if ($[4] !== collection.chain.identifier || $[5] !== selectedCurrency) {
    t3 = selectedCurrency ? [{
      asset: {
        contractAddress: selectedCurrency.contractAddress,
        chain: selectedCurrency.chainIdentifier
      }
    }] : [{
      asset: {
        contractAddress: "0x0000000000000000000000000000000000000000",
        chain: collection.chain.identifier
      }
    }];
    $[4] = collection.chain.identifier;
    $[5] = selectedCurrency;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const fromAssets = t3;
  const t4 = address;
  const t5 = walletDestination?.address;
  let t6;
  if ($[7] !== fromAssets || $[8] !== t4 || $[9] !== t5 || $[10] !== toAssets) {
    t6 = {
      address: t4,
      fromAssets,
      toAssets,
      recipient: t5
    };
    $[7] = fromAssets;
    $[8] = t4;
    $[9] = t5;
    $[10] = toAssets;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = walletDestination && !isAddressCompatible || selectedCurrencyLoading;
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      query: MintActionTimelineQuery,
      variables: t6,
      context: NETWORK_ONLY_OPERATION_CONTEXT,
      pause: t7
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [t9, refetch] = useQuery(t8);
  const {
    data,
    error,
    fetching
  } = t9;
  useSetBlockchainActionErrors(data?.swap.errors);
  const actionError = useActionErrors();
  let t10;
  if ($[15] !== collection.address || $[16] !== collection.chain.identifier || $[17] !== isCrossChain || $[18] !== onComplete) {
    t10 = async (response, receiptTransactions) => {
      if (response.action === "TRANSACTION") {
        const transactionResponse = {
          transactionIdentifier: response.transactionIdentifier,
          isCrossChain,
          toAsset: {
            contractAddress: collection.address,
            chain: collection.chain.identifier,
            tokenId: "0"
          }
        };
        onComplete(transactionResponse, receiptTransactions);
      }
    };
    $[15] = collection.address;
    $[16] = collection.chain.identifier;
    $[17] = isCrossChain;
    $[18] = onComplete;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleComplete = t10;
  if (fetching || selectedCurrencyLoading) {
    let t11;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <ActionTimelineSkeleton count={1} />;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    return t11;
  }
  if (!isAddressCompatible) {
    let t11;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <CrossEcosystemSwapNotification />;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  if (isAbstractWallet && isCrossChain) {
    let t11;
    if ($[22] !== t) {
      t11 = t("abstractWalletMintNotAllowed");
      $[22] = t;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== refetch || $[25] !== t11) {
      t12 = <ActionTimelineError message={t11} onRetry={refetch} />;
      $[24] = refetch;
      $[25] = t11;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    return t12;
  }
  if (error) {
    console.error(error);
    let t11;
    if ($[27] !== t) {
      t11 = t("error");
      $[27] = t;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== refetch || $[30] !== t11) {
      t12 = <ActionTimelineError message={t11} onRetry={refetch} />;
      $[29] = refetch;
      $[30] = t11;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    return t12;
  }
  let t11;
  if ($[32] !== data?.swap.actions) {
    t11 = data?.swap.actions ?? [];
    $[32] = data?.swap.actions;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== actionError || $[35] !== collection.chain.identifier) {
    t12 = actionError?.includes("Insufficient funds") ? collection.chain.identifier : undefined;
    $[34] = actionError;
    $[35] = collection.chain.identifier;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== handleComplete || $[38] !== refetch || $[39] !== t11 || $[40] !== t12) {
    t13 = <ActionTimeline actions={t11} insufficientFundsOnChain={t12} onAllActionsCompleted={handleComplete} onReload={refetch} />;
    $[37] = handleComplete;
    $[38] = refetch;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}
function _temp(item) {
  return {
    asset: {
      contractAddress: item.contractAddress,
      chain: item.chain.identifier,
      tokenId: item.tokenId
    },
    quantity: item.quantity.toString()
  };
}